import {
  Box,
  Flex,
  HStack,
  IconButton,
  Text,
  useDisclosure,
  Icon,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useUserContext } from "../../Contexts/userContext";
import useIcons from "../../hooks/useIcons";
import { useRoleAccess } from "../../hooks/useRoleAccess";
import MobileMenu from "./MobileMenu";
import { useNavigate } from "react-router-dom";
import useThemeColors from "../../hooks/useThemeColors";
import NotificationBell from "../Pages/Settings/NotificationSettings/NotificationBell";
import { Tooltip } from "../ui/tooltip";
import { ColorModeButton } from "../ui/color-mode";
import { Avatar } from "../ui/avatar";
import {
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem as CustomMenuItem,
  MenuItemGroup as CustomMenuItemGroup,
  MenuSeparator,
} from "../ui/menu";
import NotificationCenter from "../Pages/Settings/NotificationSettings/NotificationCenter";
import useAuth from "../../hooks/useAuth";

interface prop {
  pageName: string;
  isCollapsed: boolean;
  isMobile: boolean;
}

function TopBar({ pageName, isMobile }: prop) {
  const [greeting, setGreeting] = useState("");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { open, onOpen, onClose } = useDisclosure();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const colors = useThemeColors();
  const icons = useIcons();
  const { isAdmin } = useRoleAccess();
  const { logout } = useAuth();
  useEffect(() => {
    const updateGreeting = () => {
      const currentHour = new Date().getHours();
      if (currentHour < 12) {
        setGreeting("Good Morning");
      } else if (currentHour < 18) {
        setGreeting("Good Afternoon");
      } else {
        setGreeting("Good Evening");
      }
    };

    updateGreeting();
    const interval = setInterval(updateGreeting, 1800000); // Update every 30 minutes
    return () => clearInterval(interval);
  }, []);

  const handleUserProfile = () => {
    navigate("/profile");
  };

  const handleSelect = (selection: { value: string }) => {
    switch (selection.value) {
      case "profile":
        handleUserProfile();
        break;
      case "notifications":
        navigate("admin/notifications");
        break;
      case "settings":
        navigate("/admin/school");
        break;
      case "help":
        window.open("/help", "_blank");
        break;
      case "logout":
        logout();
        break;
      default:
        break;
    }
  };

  return (
    <Box
      as="header"
      bg={colors.background.topBar}
      borderBottomWidth="1px"
      borderBottomColor={"border.muted"}
      boxShadow="sm"
      position="sticky"
      top={0}
      zIndex={10}
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        h={{ base: "50px", md: "60px" }}
        px={{ base: 3, md: 4 }}
        maxW="100%"
      >
        {/* Left Section - Page Title */}
        <HStack>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="semibold"
            lineClamp={1}
          >
            {pageName}
          </Text>
        </HStack>

        {/* Right Section - Actions & User Info */}
        <HStack gap={3} alignItems="center">
          {/* Color Mode Switch */}
          {!isMobile && (
            <Tooltip
              content="Toggle theme"
              positioning={{ placement: "bottom" }}
            >
              <Box>
                <ColorModeButton />
              </Box>
            </Tooltip>
          )}

          {/* Help Button */}
          <Tooltip
            content="Help & Support"
            positioning={{ placement: "bottom" }}
            asChild
          >
            <IconButton
              aria-label="Help and support"
              variant="ghost"
              size="lg"
              onClick={() => window.open("/help", "_blank")}
              borderRadius="lg"
              _active={{ transform: "scale(0.95)" }}
              transition="all 0.2s"
            >
              <Icon as={icons.utility.help} />
            </IconButton>
          </Tooltip>

          {/* Dashboard Connection Status
          {!isMobile && (
            <Tooltip
              content="Dashboard connection status"
              positioning={{ placement: "bottom" }}
            >
              <Box>
                <DashboardConnectionStatus size="sm" />
              </Box>
            </Tooltip>
          )} */}

          <Tooltip
            content="Notifications"
            positioning={{ placement: "bottom" }}
            asChild
          >
            <NotificationCenter
              setOpen={setNotificationsOpen}
              open={notificationsOpen}
              isMobile={isMobile}
              trigger={<NotificationBell />}
            />
          </Tooltip>

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              aria-label="Open menu"
              variant="ghost"
              size="lg"
              onClick={onOpen}
              borderRadius="lg"
              _active={{ transform: "scale(0.95)" }}
              transition="all 0.2s"
            >
              <Icon as={icons.utility.hamburger} />
            </IconButton>
          )}

          {/* Mobile Menu */}
          {isMobile && <MobileMenu isOpen={open} onClose={onClose} />}

          {/* User Profile Dropdown */}
          {!isMobile && (
            <MenuRoot
              onSelect={handleSelect}
              positioning={{ placement: "bottom-end" }}
            >
              <MenuTrigger>
                <Avatar
                  name={user?.firstName + " " + user?.lastName}
                  src={user?.avatarUrl || ""}
                  _hover={{
                    outlineWidth: "2px",
                    outlineColor: colors.schoolAccent.focus,
                    outlineOffset: "2px",
                    outlineStyle: "solid",
                    cursor: "pointer",
                    bg: colors.background.tertiary,
                  }}
                  transition="all 0.2s"
                  flexShrink={0}
                  size="lg"
                  shape="rounded"
                />
              </MenuTrigger>
              <MenuContent>
                <Box px={4} py={3} borderBottomWidth="1px">
                  <Text fontWeight="semibold" fontSize="sm">
                    {user?.firstName} {user?.lastName}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {user?.email}
                  </Text>
                  {greeting && (
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      {greeting} 👋
                    </Text>
                  )}
                </Box>
                <CustomMenuItemGroup>
                  <CustomMenuItem value="profile">
                    <Icon as={icons.utility.shield} mr={2} /> Profile & Security
                  </CustomMenuItem>
                  <CustomMenuItem value="notifications">
                    <Icon as={icons.utility.notification} mr={2} />{" "}
                    Notifications
                  </CustomMenuItem>
                  {isAdmin() && (
                    <CustomMenuItem value="settings">
                      <Icon as={icons.utility.settings} mr={2} /> Settings
                    </CustomMenuItem>
                  )}
                </CustomMenuItemGroup>
                <MenuSeparator />
                <CustomMenuItemGroup>
                  <CustomMenuItem value="help">
                    <Icon as={icons.utility.help} mr={2} /> Help & Feedback
                  </CustomMenuItem>
                </CustomMenuItemGroup>
                <MenuSeparator />
                <CustomMenuItemGroup>
                  <CustomMenuItem color="red.500" value="logout">
                    <Icon as={icons.utility.logout} mr={2} /> Sign Out
                  </CustomMenuItem>
                </CustomMenuItemGroup>
                <MenuSeparator />
                <Box px={4} py={2}>
                  <Text fontSize="xs" color="gray.400">
                    Version 1.0.0
                  </Text>
                </Box>
              </MenuContent>
            </MenuRoot>
          )}
        </HStack>
      </Flex>
    </Box>
  );
}

export default TopBar;
